
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { DayOfWeek, TimeSlot } from '@/models/Schedule';
import { toast } from '@/components/ui/sonner';
import WorkoutTypeSelectionField from './forms/WorkoutTypeSelectionField';
import TimeSelectionFields from './forms/TimeSelectionFields';
import MaxClientsField from './forms/MaxClientsField';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import * as z from 'zod';
import { useTimeSlotValidation } from './forms/useTimeSlotValidation';

// Create a schema specifically for editing (without selectedDays)
const createEditTimeSlotSchema = (t: (key: string) => string) => {
  return z.object({
    workoutTypeId: z.string().min(1, t('schedule.form.selectWorkoutType')),
    startTime: z.string().min(1, t('schedule.form.enterValidTimes')),
    endTime: z.string().min(1, t('schedule.form.enterValidTimes')),
    maxClients: z.number().min(1, t('schedule.form.enterMaxClients')),
    autoEndTime: z.boolean().default(false),
    selectedDays: z.array(z.string()).default([]), // Not used in edit mode but needed for form compatibility
  });
};

type EditTimeSlotFormData = z.infer<
  ReturnType<typeof createEditTimeSlotSchema>
>;

interface TimeSlotEditFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timeSlot: TimeSlot | null;
  onUpdateTimeSlot: (
    id: string,
    data: {
      start_time: string;
      end_time: string;
      workout_type_id: string;
      max_clients: number;
    }
  ) => Promise<void>;
  existingTimeSlots: TimeSlot[];
}

const TimeSlotEditForm: React.FC<TimeSlotEditFormProps> = ({
  open,
  onOpenChange,
  timeSlot,
  onUpdateTimeSlot,
  existingTimeSlots,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { validateTimeRange, checkForOverlaps } = useTimeSlotValidation(
    existingTimeSlots.filter((ts) => ts.id !== timeSlot?.id)
  );

  // Create the validation function inline since we can't use hooks inside functions

  const form = useForm<EditTimeSlotFormData>({
    resolver: zodResolver(createEditTimeSlotSchema(t)),
    defaultValues: {
      workoutTypeId: '',
      startTime: '',
      endTime: '',
      maxClients: 10,
      autoEndTime: false,
      selectedDays: [],
    },
  });

  // Reset form when timeSlot changes
  useEffect(() => {
    if (timeSlot) {
      form.reset({
        workoutTypeId: timeSlot.workoutTypeId,
        startTime: timeSlot.startTime,
        endTime: timeSlot.endTime,
        maxClients: timeSlot.maxClients,
        autoEndTime: false,
        selectedDays: [],
      });
    }
  }, [timeSlot, form]);

  const onSubmit = async (data: EditTimeSlotFormData) => {
    if (!timeSlot) return;

    setIsSubmitting(true);

    try {
      // Validate time range
      console.log('Form submission started with data:', data);
      if (!validateTimeRange(data.startTime, data.endTime)) {
        console.log('Time range validation failed');
        toast.error(t('schedule.invalidTimeRange'));
        setIsSubmitting(false);
        return;
      }

      // Check for overlaps with other time slots (excluding the current one being edited)
      const hasOverlap = checkForOverlaps(
        timeSlot.day,
        data.startTime,
        data.endTime,
        data.workoutTypeId
      );

      if (hasOverlap) {
        toast.error(t('schedule.overlapError'));
        setIsSubmitting(false);
        return;
      }

      // Update time slot data for the parent component to handle
      const updateData = {
        start_time: data.startTime,
        end_time: data.endTime,
        workout_type_id: data.workoutTypeId,
        max_clients: data.maxClients,
      };

      await onUpdateTimeSlot(timeSlot.id, updateData);
      onOpenChange(false);
    } catch (error) {
      console.error('Error in form submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!timeSlot) return null;

  const formContent = (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 h-full flex flex-col"
      >
        <div className="flex-1 overflow-y-auto space-y-4">
          <WorkoutTypeSelectionField control={form.control} />

          <TimeSelectionFields
            control={form.control}
            watch={form.watch}
            setValue={form.setValue}
          />

          <MaxClientsField control={form.control} />
        </div>

        {/* Submit Buttons */}
        <div className="flex-shrink-0 space-y-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="w-full"
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting} className="w-full">
            {isSubmitting ? t('schedule.updating') : t('common.save')}
          </Button>
        </div>
      </form>
    </Form>
  );

  if (isMobile || isTablet) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix"
        >
          <SheetHeader className="p-6 border-b flex-shrink-0">
            <SheetTitle>{t('schedule.editTimeSlot')}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 p-6 overflow-hidden">{formContent}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('schedule.editTimeSlot')}</DialogTitle>
        </DialogHeader>

        {formContent}
      </DialogContent>
    </Dialog>
  );
};

export default TimeSlotEditForm;
